import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import LenisProvider from "@/components/LenisProvider";
import Layout from "@/components/Layout";
import CodeInjections from "@/components/CodeInjections";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteProvider } from "@/contexts/SiteContext";

import Home from "@/pages/Home";
import OurCompany from "@/pages/OurCompany";
import AdvisoryTeam from "@/pages/AdvisoryTeam";
import WhyPirac from "@/pages/WhyPirac";
import Framework from "@/pages/Framework";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Testimonials from "@/pages/Testimonials";
import Awards from "@/pages/Awards";
import Blogs from "@/pages/Blogs";
import BlogDetail from "@/pages/BlogDetail";
import Contact from "@/pages/Contact";
import PropertyValuation from "@/pages/PropertyValuation";
const CalculatorsHub = lazy(() => import("@/pages/calculators/Hub"));
const Mortgage = lazy(() => import("@/pages/calculators/Mortgage"));
const SalesProceeds = lazy(() => import("@/pages/calculators/SalesProceeds"));
const Purchase = lazy(() => import("@/pages/calculators/Purchase"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("@/pages/TermsOfUse"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
import PathResolver from "@/pages/PathResolver";

const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const RequirePermission = lazy(() =>
  import("@/pages/admin/AdminLayout").then((m) => ({ default: m.RequirePermission }))
);
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const BlogsList = lazy(() => import("@/pages/admin/BlogsList"));
const BlogEditor = lazy(() => import("@/pages/admin/BlogEditor"));
const CaseStudiesList = lazy(() => import("@/pages/admin/CaseStudiesList"));
const CaseStudyEditor = lazy(() => import("@/pages/admin/CaseStudyEditor"));
const TestimonialsList = lazy(() => import("@/pages/admin/TestimonialsList"));
const TestimonialEditor = lazy(() => import("@/pages/admin/TestimonialEditor"));
const AwardsList = lazy(() => import("@/pages/admin/AwardsList"));
const AwardEditor = lazy(() => import("@/pages/admin/AwardEditor"));
const EnquiriesList = lazy(() => import("@/pages/admin/EnquiriesList"));
const EnquiryDetail = lazy(() => import("@/pages/admin/EnquiryDetail"));
const SeoManager = lazy(() => import("@/pages/admin/SeoManager"));
const SeoPageEditor = lazy(() => import("@/pages/admin/SeoPageEditor"));
const RedirectsPage = lazy(() => import("@/pages/admin/Redirects"));
const CodeInjectionList = lazy(() => import("@/pages/admin/CodeInjectionList"));
const CodeInjectionEditor = lazy(() => import("@/pages/admin/CodeInjectionEditor"));
const Administrators = lazy(() => import("@/pages/admin/Administrators"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const ActivityLog = lazy(() => import("@/pages/admin/ActivityLog"));
const Account = lazy(() => import("@/pages/admin/Account"));

/**
 * Routing.
 *
 * Public routes are declared explicitly for their default paths, and the
 * catch-all hands anything else to PathResolver — which asks the server whether
 * an unrecognised path is a renamed page, a redirect, or genuinely missing.
 * That is what makes editable slugs work in a client-routed app.
 *
 * Admin routes sit under a layout that requires authentication, and each is
 * additionally wrapped in the permission it needs. The API enforces the same
 * permissions again; these wrappers only decide what to render.
 */

function App() {
  return (
    <AuthProvider>
      <SiteProvider>
        <LenisProvider>
          <BrowserRouter>
            {/* Runs page-scoped tracking scripts on client-side navigation. */}
            <CodeInjections />

            {/* Only the admin chunks suspend; the public pages are static
                imports, so this boundary never shows on a normal visit. */}
            <Suspense fallback={<div className="min-h-screen bg-[#060f1e]" />}>
            <Routes>
              {/* -------------------------------------------------- Public */}
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/our-company" element={<OurCompany />} />
                <Route path="/our-advisory-team" element={<AdvisoryTeam />} />
                <Route path="/why-pirac" element={<WhyPirac />} />
                <Route path="/framework" element={<Framework />} />
                <Route path="/case-studies" element={<CaseStudies />} />
                <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/awards" element={<Awards />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<BlogDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/property-valuation" element={<PropertyValuation />} />
                <Route path="/calculators" element={<CalculatorsHub />} />
                <Route path="/calculators/mortgage" element={<Mortgage />} />
                <Route path="/calculators/sales-proceeds" element={<SalesProceeds />} />
                <Route path="/calculators/purchase" element={<Purchase />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/disclaimer" element={<Disclaimer />} />

                {/* Custom slugs, redirects and genuine 404s all land here. */}
                <Route path="*" element={<PathResolver />} />
              </Route>

              {/* --------------------------------------------------- Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />

              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />

                <Route
                  path="blogs"
                  element={<RequirePermission permission="blogs.read"><BlogsList /></RequirePermission>}
                />
                <Route
                  path="blogs/:id"
                  element={<RequirePermission permission="blogs.read"><BlogEditor /></RequirePermission>}
                />

                <Route
                  path="case-studies"
                  element={<RequirePermission permission="caseStudies.read"><CaseStudiesList /></RequirePermission>}
                />
                <Route
                  path="case-studies/:id"
                  element={<RequirePermission permission="caseStudies.read"><CaseStudyEditor /></RequirePermission>}
                />

                <Route
                  path="testimonials"
                  element={<RequirePermission permission="testimonials.read"><TestimonialsList /></RequirePermission>}
                />
                <Route
                  path="testimonials/:id"
                  element={<RequirePermission permission="testimonials.read"><TestimonialEditor /></RequirePermission>}
                />

                <Route
                  path="awards"
                  element={<RequirePermission permission="awards.read"><AwardsList /></RequirePermission>}
                />
                <Route
                  path="awards/:id"
                  element={<RequirePermission permission="awards.read"><AwardEditor /></RequirePermission>}
                />

                <Route
                  path="enquiries"
                  element={<RequirePermission permission="enquiries.read"><EnquiriesList /></RequirePermission>}
                />
                <Route
                  path="enquiries/:id"
                  element={<RequirePermission permission="enquiries.read"><EnquiryDetail /></RequirePermission>}
                />

                <Route
                  path="seo"
                  element={<RequirePermission permission="seo.read"><SeoManager /></RequirePermission>}
                />
                <Route
                  path="seo/:key"
                  element={<RequirePermission permission="seo.read"><SeoPageEditor /></RequirePermission>}
                />
                <Route
                  path="redirects"
                  element={<RequirePermission permission="redirects.read"><RedirectsPage /></RequirePermission>}
                />

                <Route
                  path="code-injection"
                  element={<RequirePermission permission="codeInjection.read"><CodeInjectionList /></RequirePermission>}
                />
                <Route
                  path="code-injection/:id"
                  element={<RequirePermission permission="codeInjection.read"><CodeInjectionEditor /></RequirePermission>}
                />

                <Route
                  path="administrators"
                  element={<RequirePermission permission="admins.read"><Administrators /></RequirePermission>}
                />
                <Route
                  path="settings"
                  element={<RequirePermission permission="settings.read"><Settings /></RequirePermission>}
                />
                <Route
                  path="activity"
                  element={<RequirePermission permission="activity.read"><ActivityLog /></RequirePermission>}
                />
                <Route path="account" element={<Account />} />

                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Route>
            </Routes>
            </Suspense>

            <Toaster theme="dark" position="bottom-center" richColors closeButton />
          </BrowserRouter>
        </LenisProvider>
      </SiteProvider>
    </AuthProvider>
  );
}

export default App;
